/* Shared types for the conversational workspace UI */
export type CardKind = 'file' | 'command' | 'test' | 'search';
export type Verb =
  | 'viewed' | 'edited' | 'created' | 'deleted'
  | 'listed' | 'ran' | 'searched' | 'retrieved';

export interface UICard {
  type: CardKind;
  verb: Verb;
  label: string;
  path?: string;
  command?: string;
  query?: string;
  transient?: boolean;
  ok?: boolean;
}

export interface Attachment {
  kind: string;        // 'image' | 'file' | 'audio'
  name: string;
  uri?: string;
  mime?: string;
  size?: number;
}

export interface Turn {
  id: string;
  role: 'user' | 'engine' | 'system' | 'architect';
  kind: 'message' | 'bitacora' | 'question' | 'state_change' | 'result' | 'error' | 'delivery_card';
  content: string;
  attachments: Attachment[];
  detail: Record<string, any> & { card?: UICard };
  created_at: string;
  refs: Record<string, any>;
}

export interface Conversation {
  id: string;
  workspace_id: string;
  user_id: string;
  engine_state: string;
  turns: Turn[];
  pending_question?: string | null;
  pending_context: Record<string, any>;
  context_cache: Record<string, any>;
  updated_at: string;
}

export const ACTIVE_STATES = new Set([
  'engine_running', 'engine_testing', 'engine_reviewing', 'engine_deploying',
]);
export const WAITING_USER_STATES = new Set([
  'engine_waiting_user', 'engine_waiting_credits',
]);

/**
 * Sprint P0 Cierre · i18n-friendly.
 * Old exported literal map (kept for back-compat with any consumer
 * that still reads it). New consumers must call `statusPillText(t, key)`
 * from React so the label respects the current locale.
 */
export const STATUS_PILL_TEXT: Record<string, string> = {
  engine_running:    'Trabajando…',
  engine_testing:    'Ejecutando pruebas…',
  engine_reviewing:  'Evaluando resultados…',
  engine_deploying:  'Desplegando…',
  engine_waiting_user:    'Esperando tu respuesta',
  engine_waiting_credits: 'Sin créditos — esperando',
};

export function statusPillText(t: (k: string) => string, key: string): string {
  // Map backend engine states to i18n keys.
  const map: Record<string, string> = {
    engine_running:    'engine.working',
    engine_testing:    'engine.reviewing',
    engine_reviewing:  'engine.reviewing',
    engine_deploying:  'engine.building',
    engine_waiting_user:    'engine.idle',
    engine_waiting_credits: 'engine.canceled',
  };
  const k = map[key];
  if (!k) return STATUS_PILL_TEXT[key] || key;
  const localized = t(k);
  if (localized && localized !== k) return localized;
  return STATUS_PILL_TEXT[key] || key;
}
